import { useState, useEffect, useRef } from 'react';
import rrwebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';

// Custom styles to counter-scale the controls so they remain readable
// When player is scaled down, we scale the controller back up
const getPlayerControlsStyle = (scale: number) => `
  .rr-controller {
    transform: scale(${1 / scale}) !important;
    transform-origin: bottom left !important;
    width: ${100 * scale}% !important;
    margin-top: ${-80 * (1 - scale)}px !important;
  }
`;

interface UATSession {
  id: string;
  user_agent: string;
  screen_width: number;
  screen_height: number;
  page_url: string;
  started_at: string;
  ended_at: string | null;
  event_count: number;
  feedback_count: number;
}

interface UATFeedback {
  id: string;
  session_id: string;
  feedback_type: string;
  message: string;
  rating: number | null;
  page_url: string;
  created_at: string;
  user_name?: string;
  user_agent?: string;
  screen_width?: number;
  screen_height?: number;
  screenshot_count?: number;
  team_notes?: string;
}

interface UATScreenshot {
  id: string;
  filename: string;
  mime_type: string;
  file_size: number;
  captured_at?: string;
}

type ViewType = 'sessions' | 'feedback';

export const UATSessions: React.FC = () => {
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';

  const [activeView, setActiveView] = useState<ViewType>('sessions');
  const [sessions, setSessions] = useState<UATSession[]>([]);
  const [feedback, setFeedback] = useState<UATFeedback[]>([]);
  const [selectedSession, setSelectedSession] = useState<UATSession | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<UATFeedback | null>(null);
  const [screenshots, setScreenshots] = useState<UATScreenshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [playerScale, setPlayerScale] = useState(1);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [sessionToDelete, setSessionToDelete] = useState<UATSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingNotes, setEditingNotes] = useState<string>('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerWrapperRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);

  // Load sessions
  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/uat/sessions`);
      const data = await response.json();
      setSessions(data.sessions || []);
    } catch (err) {
      setError('Failed to load sessions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load feedback
  const loadFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiBaseUrl}/api/uat/feedback`);
      const data = await response.json();
      setFeedback(data.feedback || []);
    } catch (err) {
      setError('Failed to load feedback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete session and all associated data
  const deleteSession = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/uat/sessions/${sessionToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove from local state
        setSessions(sessions.filter(s => s.id !== sessionToDelete.id));
        // Clear selection if this was the selected session
        if (selectedSession?.id === sessionToDelete.id) {
          setSelectedSession(null);
        }
        setSessionToDelete(null);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete session');
      }
    } catch (err) {
      setError('Failed to delete session');
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Load session for replay
  const loadSessionReplay = async (session: UATSession) => {
    setSelectedSession(session);
    setPlayerLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/uat/sessions/${session.id}/events`);
      const data = await response.json();

      if (!data.events || data.events.length === 0) {
        setError('No events found for this session. The session may not have been recorded properly.');
        setPlayerLoading(false);
        return;
      }

      // Check if we have the required event types for rrweb-player
      // Type 4 = Meta event (required first)
      // Type 2 = Full snapshot (required for DOM reconstruction)
      const hasMeta = data.events.some((e: any) => e.type === 4);
      const hasFullSnapshot = data.events.some((e: any) => e.type === 2);

      if (!hasMeta || !hasFullSnapshot) {
        const missing = [];
        if (!hasMeta) missing.push('meta event');
        if (!hasFullSnapshot) missing.push('full DOM snapshot');
        setError(`This session is missing required data (${missing.join(', ')}). Replay not available.`);
        setPlayerLoading(false);
        return;
      }

      // Validate event structure - ensure events have required properties
      const validEvents = data.events.filter((e: any) =>
        e && typeof e === 'object' && typeof e.type === 'number' && typeof e.timestamp === 'number'
      );

      if (validEvents.length < data.events.length) {
        console.warn(`Filtered ${data.events.length - validEvents.length} malformed events`);
      }

      if (validEvents.length === 0) {
        setError('Session events are malformed. Replay not available.');
        setPlayerLoading(false);
        return;
      }

      // Destroy previous player if exists
      if (playerRef.current) {
        try {
          playerRef.current.$destroy?.();
        } catch (e) {
          // Ignore destroy errors
        }
        playerRef.current = null;
      }

      // Clear previous player container
      if (playerContainerRef.current) {
        playerContainerRef.current.innerHTML = '';
      }

      // Create new player with error handling
      try {
        // Ensure events are sorted by timestamp
        const sortedEvents = [...validEvents].sort((a: any, b: any) => a.timestamp - b.timestamp);

        console.log('[UAT] Creating player with', sortedEvents.length, 'events');
        console.log('[UAT] First 3 events:', sortedEvents.slice(0, 3).map((e: any) => ({ type: e.type, timestamp: e.timestamp })));

        // Use a small delay to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!playerContainerRef.current) {
          throw new Error('Player container not available');
        }

        // Get dimensions from the meta event (type 4) for accurate replay
        // This is more accurate than session metadata since it's what rrweb recorded
        const metaEvent = sortedEvents.find((e: any) => e.type === 4);
        const sessionWidth = metaEvent?.data?.width || session.screen_width || 1920;
        const sessionHeight = metaEvent?.data?.height || session.screen_height || 1080;

        console.log('[UAT] Using dimensions from meta event:', sessionWidth, 'x', sessionHeight);

        // Calculate scale to fit in container (approx 900px wide area)
        const containerWidth = fullscreenMode ? window.innerWidth - 40 : 900;
        const containerHeight = fullscreenMode ? window.innerHeight - 150 : 550;

        // Calculate scale to fit both width and height
        const scaleX = containerWidth / sessionWidth;
        const scaleY = containerHeight / sessionHeight;
        const scale = Math.min(scaleX, scaleY, 1); // Never scale up, only down

        setPlayerScale(scale);

        // Create player at full resolution for accuracy
        playerRef.current = new rrwebPlayer({
          target: playerContainerRef.current,
          props: {
            events: sortedEvents,
            width: sessionWidth,
            height: sessionHeight,
            autoPlay: false,
            showController: true,
            speedOption: [0.1, 0.25, 0.5, 1, 2, 4],
            // Use 1:1 scale internally for pixel-perfect accuracy
            maxScale: 1,
            skipInactive: true,
            showWarning: true,
            showDebug: false,
            // Mouse tail for better visibility
            mouseTail: {
              duration: 500,
              lineCap: 'round',
              lineWidth: 3,
              strokeStyle: 'red',
            },
            // CORS settings for loading external resources
            UNSAFE_replayCanvas: false,
            // Use precise timing
            useVirtualDom: false,
            // Ensure iframe contents are replayed correctly
            triggerFocus: false,
            // Insert styles to fix potential CSS issues
            insertStyleRules: [
              // Ensure the replay iframe doesn't have any transform issues
              'iframe { transform: none !important; }',
              // Fix any potential viewport issues
              'html, body { overflow: visible !important; }',
            ],
          },
        });

        console.log('[UAT] Player created at', sessionWidth, 'x', sessionHeight, 'with CSS scale:', scale);
      } catch (playerErr) {
        console.error('[UAT] rrweb-player error:', playerErr);
        setError(`Failed to initialize replay player: ${playerErr instanceof Error ? playerErr.message : 'Unknown error'}`);
        if (playerContainerRef.current) {
          playerContainerRef.current.innerHTML = '';
        }
      }

    } catch (err) {
      setError('Failed to load session replay');
      console.error(err);
    } finally {
      setPlayerLoading(false);
    }
  };

  // Save team notes for feedback
  const saveTeamNotes = async () => {
    if (!selectedFeedback) return;

    setIsSavingNotes(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/uat/feedback/${selectedFeedback.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_notes: editingNotes }),
      });

      if (response.ok) {
        // Update local state
        setSelectedFeedback({ ...selectedFeedback, team_notes: editingNotes });
        setFeedback(feedback.map(f =>
          f.id === selectedFeedback.id ? { ...f, team_notes: editingNotes } : f
        ));
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to save notes:', response.status, errorData);
        setError(`Failed to save notes: ${errorData.error || response.statusText}`);
      }
    } catch (err) {
      console.error('Failed to save notes (network error):', err);
      setError('Failed to save notes: Network error');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Load screenshots for feedback
  const loadScreenshots = async (feedbackItem: UATFeedback) => {
    setSelectedFeedback(feedbackItem);
    setEditingNotes(feedbackItem.team_notes || '');
    try {
      // First try to load screenshots by feedback_id (for screenshots submitted with feedback)
      const feedbackResponse = await fetch(`${apiBaseUrl}/api/uat/feedback/${feedbackItem.id}/screenshots`);
      if (feedbackResponse.ok) {
        const feedbackData = await feedbackResponse.json();
        if (feedbackData.screenshots && feedbackData.screenshots.length > 0) {
          setScreenshots(feedbackData.screenshots);
          return;
        }
      }

      // Fall back to session screenshots if no feedback screenshots found
      if (feedbackItem.session_id) {
        const sessionResponse = await fetch(`${apiBaseUrl}/api/uat/sessions/${feedbackItem.session_id}/screenshots`);
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          setScreenshots(sessionData.screenshots || []);
          return;
        }
      }

      setScreenshots([]);
    } catch (err) {
      console.error('Failed to load screenshots:', err);
      setScreenshots([]);
    }
  };

  // Inject custom styles for player controls - counter-scale them
  useEffect(() => {
    const styleId = 'uat-player-controls-style';
    let style = document.getElementById(styleId) as HTMLStyleElement;
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    // Update style content when scale changes
    style.textContent = getPlayerControlsStyle(playerScale);

    return () => {
      const s = document.getElementById(styleId);
      if (s) s.remove();
    };
  }, [playerScale]);

  // Initial load
  useEffect(() => {
    loadSessions();
    loadFeedback();
  }, []);

  // View change
  useEffect(() => {
    if (activeView === 'sessions') {
      loadSessions();
    } else {
      loadFeedback();
    }
  }, [activeView]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return 'In progress';
    const duration = new Date(end).getTime() - new Date(start).getTime();
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getFeedbackTypeStyle = (type: string) => {
    switch (type) {
      case 'bug': return 'bg-red-100 text-red-800';
      case 'suggestion': return 'bg-blue-100 text-blue-800';
      case 'confusion': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-zinc-100 text-zinc-800';
    }
  };

  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return '🐛';
      case 'suggestion': return '💡';
      case 'confusion': return '😕';
      default: return '💬';
    }
  };

  // Group feedback by session
  const groupFeedbackBySession = (feedbackList: UATFeedback[]) => {
    const grouped: Map<string, UATFeedback[]> = new Map();
    const noSession: UATFeedback[] = [];

    for (const item of feedbackList) {
      if (item.session_id) {
        const existing = grouped.get(item.session_id) || [];
        existing.push(item);
        grouped.set(item.session_id, existing);
      } else {
        noSession.push(item);
      }
    }

    // Convert to array and sort by most recent feedback in each group
    const sortedGroups = Array.from(grouped.entries())
      .map(([sessionId, items]) => ({
        sessionId,
        items: items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
        latestDate: Math.max(...items.map(i => new Date(i.created_at).getTime())),
      }))
      .sort((a, b) => b.latestDate - a.latestDate);

    return { grouped: sortedGroups, noSession };
  };

  const toggleSessionExpand = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const { grouped: groupedFeedback, noSession: ungroupedFeedback } = groupFeedbackBySession(feedback);

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="flex gap-2 border-b border-zinc-200 pb-4">
        <button
          type="button"
          onClick={() => setActiveView('sessions')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeView === 'sessions'
              ? 'bg-zinc-900 text-white'
              : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-300'
          }`}
        >
          Sessions ({sessions.filter(s => s.event_count > 0).length})
        </button>
        <button
          type="button"
          onClick={() => setActiveView('feedback')}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            activeView === 'feedback'
              ? 'bg-zinc-900 text-white'
              : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-300'
          }`}
        >
          Feedback ({feedback.length})
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Sessions View */}
      {activeView === 'sessions' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sessions List */}
          <div className="lg:col-span-1 border border-zinc-200 rounded-lg bg-white overflow-hidden">
            <div className="p-4 border-b border-zinc-200 flex justify-between items-center bg-zinc-50">
              <h3 className="font-semibold text-zinc-900">Sessions</h3>
              <button
                type="button"
                onClick={loadSessions}
                className="text-sm px-3 py-1 bg-zinc-100 hover:bg-zinc-200 rounded text-zinc-700"
              >
                Refresh
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-zinc-500">Loading...</div>
              ) : sessions.filter(s => s.event_count > 0).length === 0 ? (
                <div className="p-8 text-center text-zinc-500">No sessions found</div>
              ) : (
                sessions.filter(s => s.event_count > 0).map((session) => (
                  <div
                    key={session.id}
                    onClick={() => loadSessionReplay(session)}
                    className={`p-4 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${
                      selectedSession?.id === session.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-mono text-zinc-500">
                        {session.id.slice(-12)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          session.event_count > 0 ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-500'
                        }`}>
                          {session.event_count} events
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSessionToDelete(session);
                          }}
                          className="text-zinc-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete session"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-zinc-800">
                      {formatDate(session.started_at)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {session.screen_width}x{session.screen_height} • {formatDuration(session.started_at, session.ended_at)}
                    </div>
                    {session.feedback_count > 0 && (
                      <div className="text-xs text-amber-600 mt-1 font-medium">
                        📝 {session.feedback_count} feedback
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Player */}
          <div className={`${fullscreenMode ? 'fixed inset-0 z-50 bg-white' : 'lg:col-span-2'} border border-zinc-200 rounded-lg bg-white overflow-hidden`}>
            <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-zinc-900">Session Replay</h3>
                {selectedSession && (
                  <p className="text-xs text-zinc-500 mt-1 truncate max-w-md">
                    {selectedSession.page_url} ({selectedSession.screen_width}x{selectedSession.screen_height})
                  </p>
                )}
              </div>
              {selectedSession && !playerLoading && (
                <button
                  type="button"
                  onClick={() => setFullscreenMode(!fullscreenMode)}
                  className="text-sm px-3 py-1 bg-zinc-100 hover:bg-zinc-200 rounded text-zinc-700"
                >
                  {fullscreenMode ? 'Exit Fullscreen' : 'Fullscreen'}
                </button>
              )}
            </div>
            <div className={`p-4 ${fullscreenMode ? 'h-[calc(100vh-80px)] overflow-auto' : ''}`}>
              {!selectedSession && (
                <div className="h-[550px] flex items-center justify-center text-zinc-400 bg-zinc-50 rounded-lg">
                  Select a session to replay
                </div>
              )}
              {selectedSession && playerLoading && (
                <div className="h-[550px] flex flex-col items-center justify-center text-zinc-400 bg-zinc-50 rounded-lg">
                  <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-600 rounded-full animate-spin mb-4"></div>
                  Loading replay...
                </div>
              )}
              {/* Player wrapper with CSS transform for scaling while maintaining accuracy */}
              <div
                ref={playerWrapperRef}
                className={`${!selectedSession || playerLoading ? 'hidden' : ''}`}
                style={{
                  width: selectedSession ? selectedSession.screen_width * playerScale : 'auto',
                  height: selectedSession ? (selectedSession.screen_height * playerScale) + 80 : 'auto', // +80 for controls
                  overflow: 'hidden',
                  margin: '0 auto',
                }}
              >
                <div
                  ref={playerContainerRef}
                  style={{
                    transform: `scale(${playerScale})`,
                    transformOrigin: 'top left',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback View */}
      {activeView === 'feedback' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feedback List */}
          <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
            <div className="p-4 border-b border-zinc-200 flex justify-between items-center bg-zinc-50">
              <h3 className="font-semibold text-zinc-900">User Feedback</h3>
              <button
                type="button"
                onClick={loadFeedback}
                className="text-sm px-3 py-1 bg-zinc-100 hover:bg-zinc-200 rounded text-zinc-700"
              >
                Refresh
              </button>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-zinc-500">Loading...</div>
              ) : feedback.length === 0 ? (
                <div className="p-8 text-center text-zinc-500">No feedback found</div>
              ) : (
                <>
                  {/* Grouped feedback by session */}
                  {groupedFeedback.map(({ sessionId, items }) => (
                    <div key={sessionId} className="border-b border-zinc-200">
                      {/* Session header - collapsible */}
                      <div
                        onClick={() => toggleSessionExpand(sessionId)}
                        className="p-3 bg-zinc-50 cursor-pointer hover:bg-zinc-100 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400">
                            {expandedSessions.has(sessionId) ? '−' : '+'}
                          </span>
                          <span className="font-mono text-xs text-zinc-600">
                            Session ...{sessionId.slice(-8)}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                            {items.length} feedback{items.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <span className="text-xs text-zinc-500">
                          {formatDate(items[0].created_at)}
                        </span>
                      </div>

                      {/* Feedback items in session */}
                      {expandedSessions.has(sessionId) && items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => loadScreenshots(item)}
                          className={`p-4 border-t border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ml-4 ${
                            selectedFeedback?.id === item.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${getFeedbackTypeStyle(item.feedback_type)}`}>
                              {getFeedbackTypeIcon(item.feedback_type)} {item.feedback_type}
                            </span>
                            {item.rating && (
                              <span className="text-amber-500 text-sm">
                                {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                              </span>
                            )}
                            {item.user_name && (
                              <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded">
                                {item.user_name}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-800 line-clamp-2">{item.message}</p>
                          <div className="text-xs text-zinc-500 mt-2">
                            {formatDate(item.created_at)}
                            {item.screenshot_count && item.screenshot_count > 0 && (
                              <span className="ml-2 text-blue-600">📷 {item.screenshot_count}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Ungrouped feedback (no session) */}
                  {ungroupedFeedback.length > 0 && (
                    <div className="border-b border-zinc-200">
                      <div className="p-3 bg-zinc-50">
                        <span className="text-xs text-zinc-500">No session ({ungroupedFeedback.length})</span>
                      </div>
                      {ungroupedFeedback.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => loadScreenshots(item)}
                          className={`p-4 border-t border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${
                            selectedFeedback?.id === item.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${getFeedbackTypeStyle(item.feedback_type)}`}>
                              {getFeedbackTypeIcon(item.feedback_type)} {item.feedback_type}
                            </span>
                            {item.rating && (
                              <span className="text-amber-500 text-sm">
                                {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                              </span>
                            )}
                            {item.user_name && (
                              <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded">
                                {item.user_name}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-800 line-clamp-2">{item.message}</p>
                          <div className="text-xs text-zinc-500 mt-2">
                            {formatDate(item.created_at)}
                            {item.screenshot_count && item.screenshot_count > 0 && (
                              <span className="ml-2 text-blue-600">📷 {item.screenshot_count}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Feedback Detail */}
          <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50">
              <h3 className="font-semibold text-zinc-900">Feedback Details</h3>
            </div>
            <div className="p-4">
              {!selectedFeedback ? (
                <div className="h-[400px] flex items-center justify-center text-zinc-400 bg-zinc-50 rounded-lg">
                  Select feedback to view details
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Feedback Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded ${getFeedbackTypeStyle(selectedFeedback.feedback_type)}`}>
                        {getFeedbackTypeIcon(selectedFeedback.feedback_type)} {selectedFeedback.feedback_type}
                      </span>
                      {selectedFeedback.rating && (
                        <span className="text-amber-500 text-lg">
                          {'★'.repeat(selectedFeedback.rating)}{'☆'.repeat(5 - selectedFeedback.rating)}
                        </span>
                      )}
                      {selectedFeedback.user_name && (
                        <span className="text-sm text-zinc-700 bg-zinc-100 px-3 py-1 rounded">
                          From: {selectedFeedback.user_name}
                        </span>
                      )}
                    </div>

                    <div className="bg-zinc-50 p-4 rounded-lg border border-zinc-200">
                      <p className="text-zinc-800 whitespace-pre-wrap">{selectedFeedback.message}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500 text-xs">Submitted:</span>
                        <p className="text-zinc-800">{formatDate(selectedFeedback.created_at)}</p>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs">Page:</span>
                        <p className="text-zinc-800 truncate text-xs">{selectedFeedback.page_url}</p>
                      </div>
                      {selectedFeedback.screen_width && (
                        <div>
                          <span className="text-zinc-500 text-xs">Screen:</span>
                          <p className="text-zinc-800">{selectedFeedback.screen_width}x{selectedFeedback.screen_height}</p>
                        </div>
                      )}
                      {selectedFeedback.session_id && (
                        <div>
                          <span className="text-zinc-500 text-xs">Session:</span>
                          <p className="text-zinc-800 font-mono text-xs">{selectedFeedback.session_id.slice(-12)}</p>
                        </div>
                      )}
                    </div>

                    {/* View Session Button */}
                    {selectedFeedback.session_id && (
                      <button
                        type="button"
                        onClick={() => {
                          const session = sessions.find(s => s.id === selectedFeedback.session_id);
                          if (session) {
                            setActiveView('sessions');
                            setTimeout(() => loadSessionReplay(session), 100);
                          }
                        }}
                        className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white font-medium transition-colors"
                      >
                        View Session Replay
                      </button>
                    )}

                    {/* Team Notes */}
                    <div className="border-t border-zinc-200 pt-4 mt-4">
                      <h4 className="font-medium mb-2 text-zinc-700 text-sm">Team Notes</h4>
                      <textarea
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder="Add internal notes about this feedback..."
                        className="w-full h-24 px-3 py-2 border border-zinc-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-zinc-500">
                          {editingNotes !== (selectedFeedback.team_notes || '') && '• Unsaved changes'}
                        </span>
                        <button
                          type="button"
                          onClick={saveTeamNotes}
                          disabled={isSavingNotes || editingNotes === (selectedFeedback.team_notes || '')}
                          className="px-4 py-1.5 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300 disabled:cursor-not-allowed rounded text-sm text-white font-medium transition-colors"
                        >
                          {isSavingNotes ? 'Saving...' : 'Save Notes'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Screenshots */}
                  {screenshots.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-zinc-700 text-sm">Screenshots</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {screenshots.map((screenshot) => (
                          <a
                            key={screenshot.id}
                            href={`${apiBaseUrl}/api/uat/screenshots/${screenshot.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block bg-zinc-100 rounded overflow-hidden hover:ring-2 ring-blue-500 transition-all"
                          >
                            <img
                              src={`${apiBaseUrl}/api/uat/screenshots/${screenshot.id}`}
                              alt={screenshot.filename}
                              className="w-full h-24 object-cover"
                            />
                            <div className="p-2 text-xs text-zinc-500 truncate">
                              {screenshot.filename}
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {sessionToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-zinc-900">Delete Session</h3>
              </div>

              <p className="text-zinc-600 mb-2">
                Are you sure you want to delete this session?
              </p>
              <p className="text-sm text-zinc-500 mb-4">
                This will permanently delete:
              </p>
              <ul className="text-sm text-zinc-600 mb-4 ml-4 list-disc space-y-1">
                <li>Session <span className="font-mono text-xs">{sessionToDelete.id.slice(-12)}</span></li>
                <li>{sessionToDelete.event_count} recorded events</li>
                {sessionToDelete.feedback_count > 0 && (
                  <li>{sessionToDelete.feedback_count} feedback item(s) and screenshots</li>
                )}
                <li>All associated replay data</li>
              </ul>
              <p className="text-sm text-red-600 font-medium">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-zinc-50 border-t border-zinc-200">
              <button
                onClick={() => setSessionToDelete(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteSession}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete Session'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UATSessions;
